<template>
  <div>
    <div v-if="qboConnectionLoading" class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
      <span class="i-heroicons-arrow-path text-slate-500 size-8 animate-spin" />
      <p class="text-sm text-center text-slate-500 pt-4">
        Loading...
      </p>
    </div>

    <div v-else-if="!qboConnectionError && qboConnection" class="settings-quickbooks">
      <h2 class="text-lg font-semibold text-slate-900 mb-6">QuickBooks Settings</h2>

      <!-- Connection Status Section -->
      <div class="mb-6 p-4 border rounded-lg">
        <h3 class="text-md font-semibold text-slate-900 mb-4">Connection Status</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <span class="text-sm font-medium text-slate-700">Status</span>
            <div class="flex flex-col gap-2 mt-1">
              <BaseBadge :variant="connectionStatus.variant" class="p-2 w-fit">
                {{ connectionStatus.text }}
              </BaseBadge>
              <span v-if="isQboConnected && tokenExpirationDate" class="text-xs text-slate-500">
                Token expires: {{ tokenExpirationDate }}
              </span>
            </div>
          </div>

          <div>
            <span class="text-sm font-medium text-slate-700">Connected Since</span>
            <div class="mt-1">
              <span class="text-sm">{{ connectionDateFormatted }}</span>
            </div>
          </div>

          <div>
            <span class="text-sm font-medium text-slate-700">Actions</span>
            <div class="mt-1">
              <BaseButton
                v-if="!isQboConnected || connectionStatus.variant === 'error'"
                variant="primary"
                size="sm"
                :class="{ 'cursor-not-allowed': loading }"
                :disabled="loading"
                @click.prevent="connectQBO"
              >
                <div class="flex justify-center items-center gap-2">
                  <span>{{ connectionStatus.variant === 'error' && connectionStatus.text.includes('Expired') ? 'Reconnect' : 'Connect' }}</span>
                  <span v-if="loading" class="i-heroicons-arrow-path animate-spin" />
                </div>
              </BaseButton>

              <BaseButton
                v-else
                variant="danger"
                size="sm"
                :class="{ 'cursor-not-allowed': loading }"
                :disabled="loading"
                @click.prevent="revokeQBO"
              >
                <div class="flex justify-center items-center gap-2">
                  <span>Disconnect</span>
                  <span v-if="loading" class="i-heroicons-arrow-path animate-spin" />
                </div>
              </BaseButton>
            </div>
          </div>
        </div>
      </div>

      <!-- Sync Configuration Section -->
      <div v-if="isQboConnected && connectionStatus.variant !== 'error'" class="mb-6 p-4 border rounded-lg">
        <h3 class="text-md font-semibold text-slate-900 mb-4">Sync Configuration</h3>
        
        <!-- Main Sync Toggles -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div class="p-4 border rounded-lg">
              <div class="flex items-center justify-between mb-2">
                <span class="font-medium">Auto-Sync</span>
                <BaseSwitch v-model="syncConfig.autoSync" @change="updateSyncConfig" />
              </div>
              <p class="text-sm text-slate-500">Automatically sync changes to QuickBooks</p>
            </div>
            
            <div class="p-4 border rounded-lg">
              <div class="flex items-center justify-between mb-2">
                <span class="font-medium">Two-Way Sync</span>
                <BaseSwitch v-model="syncConfig.twoWaySync" @change="updateSyncConfig" />
              </div>
              <p class="text-sm text-slate-500">Import changes from QuickBooks</p>
            </div>
        </div>

        <!-- Entity Sync Controls -->
        <div class="space-y-4">
          <div class="p-4 border rounded-lg">
            <h4 class="font-medium mb-3">Entity Sync Settings</h4>
            <div class="space-y-3">
              <div v-for="entity in syncEntities" :key="entity.key" class="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-1">
                    <span class="font-medium">{{ entity.label }}</span>
                    <BaseBadge :variant="getSyncStatus(entity.key).variant" class="text-xs">
                      {{ getSyncStatus(entity.key).text }}
                    </BaseBadge>
                  </div>
                  <p class="text-sm text-slate-500">{{ entity.description }}</p>
                  <div v-if="syncConfig.lastSync?.[entity.key]" class="text-xs text-slate-400 mt-1">
                    Last synced: {{ formatDate(new Date(syncConfig.lastSync[entity.key])) }}
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <BaseSwitch v-model="syncConfig.entities[entity.key]" @change="updateSyncConfig" />
                  <BaseButton
                    size="sm"
                    variant="secondary"
                    :loading="syncing[entity.key + '_to']"
                    :disabled="!syncConfig.entities[entity.key]"
                    @click="manualSyncTo(entity.key)"
                  >
                    Export
                  </BaseButton>
                  <BaseButton
                    size="sm"
                    variant="secondary"
                    :loading="syncing[entity.key + '_from']"
                    :disabled="!syncConfig.entities[entity.key] || !syncConfig.twoWaySync"
                    @click="manualSyncFrom(entity.key)"
                  >
                    Import
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Bulk Sync Actions -->
        <div class="mt-6 p-4 border rounded-lg">
          <h4 class="font-medium mb-3">Bulk Operations</h4>
          <div class="flex flex-wrap gap-2">
            <BaseButton
              :loading="syncing.all_to"
              class="flex items-center gap-2"
              @click="syncAllTo"
            >
              <span class="i-heroicons-arrow-up-tray size-4" />
              Export All to QuickBooks
            </BaseButton>
            <BaseButton
              :loading="syncing.all_from"
              :disabled="!syncConfig.twoWaySync"
              class="flex items-center gap-2"
              @click="syncAllFrom"
            >
              <span class="i-heroicons-arrow-down-tray size-4" />
              Import All from QuickBooks
            </BaseButton>
          </div>
        </div>
      </div>

      <!-- Sync Logs Section -->
      <div v-if="isQboConnected && connectionStatus.variant !== 'error'" class="mb-6 p-4 border rounded-lg">
        <div class="flex items-center justify-between mb-4">
          <h4 class="font-medium">Sync History</h4>
          <BaseButton size="sm" variant="secondary" @click="refreshSyncLogs">
            <span class="i-heroicons-arrow-path size-4" />
            Refresh
          </BaseButton>
        </div>
        
        <BaseTable
          :items="syncLogs ?? []"
          :fields="syncLogFields"
          :is-loading="syncLogsLoading"
          class="text-sm"
        >
          <template #timestamp-data="{ row }">
            <span class="text-xs">{{ formatDate(new Date(row.timestamp)) }}</span>
          </template>
          
          <template #entity-data="{ row }">
            <span class="capitalize font-medium">{{ row.entity }}</span>
          </template>
          
          <template #operation-data="{ row }">
            <BaseBadge
              :variant="row.operation === 'import' ? 'info' : row.operation === 'export' ? 'secondary' : 'base'"
              class="text-xs capitalize"
            >
              {{ row.operation }}
            </BaseBadge>
          </template>
          
          <template #status-data="{ row }">
            <BaseBadge
              :variant="row.status === 'success' ? 'success' : row.status === 'error' ? 'error' : 'warning'"
              class="text-xs capitalize"
            >
              {{ row.status }}
            </BaseBadge>
          </template>
          
          <template #error-data="{ row }">
            <span v-if="row.error" class="text-xs text-red-600 truncate max-w-xs" :title="row.error">
              {{ row.error }}
            </span>
            <span v-else class="text-xs text-slate-400">-</span>
          </template>
        </BaseTable>
      </div>

      <!-- Unconverted Projects Section (existing) -->
      <div v-if="isQboConnected && connectionStatus.variant !== 'error'" class="mb-4 p-4 border rounded-lg">
        <h4 class="font-medium mb-2">Unconverted Projects</h4>
        <p class="mb-4 text-sm text-slate-500">
          The following projects were synced with QuickBooks but due to API limitations you'll need to login into your account to finalize the conversion to projects.
        </p>

        <BaseTable
          :is-loading="projectsLoading"
          :items="unconvertedProjectsQuery ?? []"
          :fields="tableFields"
          class="text-sm"
        >
          <template #DisplayName-data="data">
            <span class="font-semibold text-slate-950">{{ data.row.DisplayName }}</span>
          </template>
        </BaseTable>
      </div>
    </div>

    <EmptyState v-else>
      <template #icon>
        <span class="i-heroicons-exclamation-circle text-red-500 size-8"></span>
      </template>
      <template #title>
        <h2 class="text-lg font-semibold text-center text-slate-900">Error</h2>
      </template>
      <template #description>
        <p class="text-sm text-center text-slate-500 pt-4">
          An error occurred while trying to load the QuickBooks settings
        </p>
      </template>
    </EmptyState>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { httpsCallable } from 'firebase/functions'
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query'
import FirebaseService from '@/services/FirebaseService'
import SettingService from '@/services/SettingService'
import BaseBadge from '@/components/base/BaseBadge.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseSwitch from '@/components/base/BaseSwitch.vue'
import EmptyState from '@/components/app/EmptyState.vue'
import { formatDate } from '@/utils'

const { notify } = useNotification()
const route = useRoute()
const router = useRouter()
const loading = ref(false)

// Sync entities configuration
const syncEntities = [
  { 
    key: 'customers', 
    label: 'Customers', 
    description: 'Sync customer information and contacts' 
  },
  { 
    key: 'projects', 
    label: 'Projects', 
    description: 'Sync projects as jobs in QuickBooks' 
  },
  { 
    key: 'bills', 
    label: 'Bills', 
    description: 'Sync vendor bills and expenses' 
  },
  { 
    key: 'invoices', 
    label: 'Invoices', 
    description: 'Sync customer invoices and payments' 
  }
]

// Sync state management
const syncing = ref<Record<string, boolean>>({})
const syncConfig = ref({
  autoSync: true,
  twoWaySync: true,
  entities: {
    customers: true,
    projects: true,
    bills: true,
    invoices: true
  },
  lastSync: {}
})

// Table configurations
const tableFields = [
  { key: 'DisplayName', label: 'Name' },
  { key: 'IsProject', label: 'Project Status' }
]

const syncLogFields = [
  { key: 'timestamp', label: 'Time' },
  { key: 'entity', label: 'Entity' },
  { key: 'operation', label: 'Operation' },
  { key: 'status', label: 'Status' },
  { key: 'error', label: 'Error' }
]

const queryClient = useQueryClient()

// Connection status queries
const {data:qboConnection, isPending:qboConnectionLoading, isError:qboConnectionError} = useQuery({
  queryKey: ['qboConnectionStatus'],
  queryFn: async () => await new SettingService().find('intuit')
})

// Computed properties for connection status
const isQboConnected = computed(() => {
  const data = qboConnection.value
  return !!(data?.access_token && data?.refresh_token && data?.realmId)
})

const connectionStatus = computed(() => {
  const data = qboConnection.value
  
  if (!data?.access_token || !data?.refresh_token || !data?.realmId) {
    return { variant: 'error' as const, text: 'Not Connected' }
  }
  
  if (data.tokenExpire && data.tokenExpire < Date.now()) {
    return { variant: 'error' as const, text: 'Token Expired - Reconnection Required' }
  }
  
  return { variant: 'success' as const, text: 'Connected' }
})

// Sync configuration queries
const { data: syncConfigData } = useQuery({
  queryKey: ['qboSyncConfig'],
  queryFn: async () => {
    const functions = await FirebaseService.functions()
    const callable = httpsCallable(functions, 'qboSync-getSyncConfigV2')
    const response = await callable()
    return response.data as any
  },
  enabled: computed(() => isQboConnected.value && connectionStatus.value.variant !== 'error')
})

// Sync logs query
const { data: syncLogs, isLoading: syncLogsLoading, refetch: refreshSyncLogs } = useQuery({
  queryKey: ['qboSyncLogs'],
  queryFn: async () => {
    const functions = await FirebaseService.functions()
    const callable = httpsCallable(functions, 'qboSync-getSyncLogsV2')
    const response = await callable({ limit: 50 })
    return response.data as any[]
  },
  enabled: computed(() => isQboConnected.value && connectionStatus.value.variant !== 'error')
})

// Watch for sync config updates
watch(syncConfigData, (newData) => {
  if (newData) {
    syncConfig.value = { ...syncConfig.value, ...newData }
  }
}, { immediate: true })

const tokenExpirationDate = computed(() => {
  const data = qboConnection.value
  if (!data?.tokenExpire || !isQboConnected.value) return null
  
  if (data.tokenExpire < Date.now()) return null
  
  return new Date(data.tokenExpire).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
})

const connectionDateFormatted = computed(() => {
  const date = qboConnection.value?.connectionDate
  return date ? new Date(date).toLocaleDateString() : 'N/A'
})

// Sync status helpers
const getSyncStatus = (entityKey: string) => {
  const lastSync = syncConfig.value.lastSync?.[entityKey]
  if (!lastSync) {
    return { variant: 'secondary' as const, text: 'Never Synced' }
  }
  
  const hoursSinceSync = (Date.now() - lastSync) / (1000 * 60 * 60)
  if (hoursSinceSync < 1) {
    return { variant: 'success' as const, text: 'Recently Synced' }
  } else if (hoursSinceSync < 24) {
    return { variant: 'info' as const, text: 'Synced Today' }
  } else {
    return { variant: 'warning' as const, text: 'Needs Sync' }
  }
}

// Unconverted projects query
const {data:unconvertedProjectsQuery, isPending:projectsLoading } = useQuery({
  queryKey: ['qboUnconvertedProjects'],
  queryFn: async () => {
    if (!isQboConnected.value) return []
    
    const data = qboConnection.value
    if (data?.tokenExpire && data.tokenExpire < Date.now()) {
      throw new Error('QuickBooks token has expired. Please reconnect your account.')
    }
    
    const functions = await FirebaseService.functions()
    const callable = httpsCallable(functions, 'qbo-getProjectsUnconvertedV2')
    const res = await callable()
    return res.data['Customer'] as any[]
  },
  enabled: computed(() => {
    const data = qboConnection.value
    return isQboConnected.value && (!data?.tokenExpire || data.tokenExpire > Date.now())
  })
})

// Sync configuration mutations
const { mutate: updateSyncConfigMutation } = useMutation({
  mutationFn: async (config: any) => {
    const functions = await FirebaseService.functions()
    const callable = httpsCallable(functions, 'qboSync-updateSyncConfigV2')
    await callable(config)
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['qboSyncConfig'] })
  },
  onError: (error: any) => {
    notify({ type: 'error', title: 'Error', text: error.message || 'Failed to update sync configuration' })
  }
})

// Manual sync mutations
const { mutate: syncEntityToQB } = useMutation({
  mutationFn: async ({ entity }: { entity: string }) => {
    const functions = await FirebaseService.functions()
    const callable = httpsCallable(functions, 'qboSync-syncEntityFromQBV2')
    await callable({ entity })
  },
  onSuccess: (_, { entity }) => {
    notify({ type: 'success', title: 'Success', text: `${entity} exported successfully` })
    queryClient.invalidateQueries({ queryKey: ['qboSyncLogs'] })
    queryClient.invalidateQueries({ queryKey: ['qboSyncConfig'] })
  },
  onError: (error: any, { entity }) => {
    notify({ type: 'error', title: 'Export Failed', text: `Failed to export ${entity}: ${error.message}` })
  }
})

const { mutate: syncEntityFromQB } = useMutation({
  mutationFn: async ({ entity }: { entity: string }) => {
    const functions = await FirebaseService.functions()
    const callable = httpsCallable(functions, 'qboSync-syncEntityFromQBV2')
    await callable({ entity })
  },
  onSuccess: (_, { entity }) => {
    notify({ type: 'success', title: 'Success', text: `${entity} imported successfully` })
    queryClient.invalidateQueries({ queryKey: ['qboSyncLogs'] })
    queryClient.invalidateQueries({ queryKey: ['qboSyncConfig'] })
  },
  onError: (error: any, { entity }) => {
    notify({ type: 'error', title: 'Import Failed', text: `Failed to import ${entity}: ${error.message}` })
  }
})

const { mutate: syncAllToQB } = useMutation({
  mutationFn: async () => {
    const functions = await FirebaseService.functions()
    const callable = httpsCallable(functions, 'qboSync-syncAllToQBV2')
    await callable()
  },
  onSuccess: () => {
    notify({ type: 'success', title: 'Success', text: 'All entities exported to QuickBooks' })
    queryClient.invalidateQueries({ queryKey: ['qboSyncLogs'] })
    queryClient.invalidateQueries({ queryKey: ['qboSyncConfig'] })
  },
  onError: (error: any) => {
    notify({ type: 'error', title: 'Export Failed', text: `Failed to export all entities: ${error.message}` })
  }
})

const { mutate: syncAllFromQB } = useMutation({
  mutationFn: async () => {
    const functions = await FirebaseService.functions()
    const callable = httpsCallable(functions, 'qboSync-syncAllFromQBV2')
    await callable()
  },
  onSuccess: () => {
    notify({ type: 'success', title: 'Success', text: 'All entities imported from QuickBooks' })
    queryClient.invalidateQueries({ queryKey: ['qboSyncLogs'] })
    queryClient.invalidateQueries({ queryKey: ['qboSyncConfig'] })
  },
  onError: (error: any) => {
    notify({ type: 'error', title: 'Import Failed', text: `Failed to import all entities: ${error.message}` })
  }
})

// Event handlers
const updateSyncConfig = () => {
  updateSyncConfigMutation(syncConfig.value)
}

const manualSyncTo = (entity: string) => {
  syncing.value[entity + '_to'] = true
  syncEntityToQB({ entity }, {
    onSettled: () => {
      syncing.value[entity + '_to'] = false
    }
  })
}

const manualSyncFrom = (entity: string) => {
  syncing.value[entity + '_from'] = true
  syncEntityFromQB({ entity }, {
    onSettled: () => {
      syncing.value[entity + '_from'] = false
    }
  })
}

const syncAllTo = () => {
  syncing.value.all_to = true
  syncAllToQB(undefined, {
    onSettled: () => {
      syncing.value.all_to = false
    }
  })
}

const syncAllFrom = () => {
  syncing.value.all_from = true
  syncAllFromQB(undefined, {
    onSettled: () => {
      syncing.value.all_from = false
    }
  })
}

// Connection management (existing functions)
const getTokens = async (realmId: string) => {
  loading.value = true
  const functions = await FirebaseService.functions()
  const redirectUri = `${window.location.origin}${window.location.pathname}`
  const urlParams = window.location.pathname + window.location.search
  const getTokens = httpsCallable(functions, 'qbo-getTokensV2')

  await getTokens({ redirectUri, parsedRedirect: urlParams, realmId })
    .then(async() => {
      loading.value = true
      notify({ type: 'success', title: 'Success', text: 'QuickBooks connection established!' })
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['qboConnectionStatus'] })
        queryClient.invalidateQueries({ queryKey: ['qboUnconvertedProjects'] })
        router.replace({ name: 'QuickBooksSettings' })
      }, 100)
    })
    .catch(error => {
      notify({ type: 'error', title: 'Error', text: error.message })
    })
    .finally(() => {
      loading.value = false
    })
}

let setimeoutId: NodeJS.Timeout | null = null

const connectQBO = async () => {
  loading.value = true
  try {
    const functions = await FirebaseService.functions()
    const redirectUri = `${window.location.origin}${window.location.pathname}`
    const getAuthURI = httpsCallable(functions, 'qbo-getAuthURIV2')

    const result = await getAuthURI({ redirectUri })
    if (result) window.location.href = result.data as string
  } catch (error: any) {
    const errorMessage = getErrorMessage(error)
    notify({ type: 'error', title: 'Connection Error', text: errorMessage })
  } finally {
    loading.value = false
  }
}

const revokeQBO = async () => {
  loading.value = true
  try {
    const functions = await FirebaseService.functions()
    const revoke = httpsCallable(functions, 'qbo-tokensRevokeV2')

    await revoke()
    notify({ type: 'success', title: 'Success', text: 'QuickBooks connection revoked!' })
    queryClient.invalidateQueries({ queryKey: ['qboConnectionStatus'] })
    queryClient.invalidateQueries({ queryKey: ['qboUnconvertedProjects'] })
  } catch (error: any) {
    const errorMessage = getErrorMessage(error)
    notify({ type: 'error', title: 'Disconnect Error', text: errorMessage })
  } finally {
    loading.value = false
  }
}

const getErrorMessage = (error: any): string => {
  const message = error.message || error.code || 'Unknown error'
  
  if (error.details?.expired || message.includes('expired') || message.includes('Token has expired')) {
    return 'Your QuickBooks connection has expired. Please reconnect your account to continue using QuickBooks integration.'
  } else if (message.includes('failed-precondition') && message.includes('reconnect')) {
    return 'QuickBooks connection issue detected. Please reconnect your account.'
  } else if (message.includes('invalid')) {
    return 'Invalid QuickBooks credentials. Please try connecting again.'
  } else if (message.includes('network') || message.includes('timeout')) {
    return 'Network error. Please check your connection and try again.'
  } else if (message.includes('unauthenticated')) {
    return 'You need to be logged in to manage QuickBooks settings.'
  } else if (message.includes('Circuit breaker is OPEN')) {
    return 'QuickBooks service is temporarily unavailable. Please try again in a few minutes.'
  }
  
  return `QuickBooks error: ${message}`
}

const handleVisibilityChange = () => {
  if (!document.hidden && isQboConnected.value) {
    queryClient.invalidateQueries({ queryKey: ['qboConnectionStatus'] })
  }
}

onMounted(async () => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
  
  const query = route.query
  const code = query.code as string
  const realmId = query.realmId as string

  if (code && realmId) {
    setimeoutId = setTimeout(async () => {
      await getTokens(realmId)
    }, 1000)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (setimeoutId) {
    clearTimeout(setimeoutId)
  }
})
</script>
